import {
  deleteNotificationById,
  publishNotification,
} from "../../../Redux/Notifications/NotificationsActions";
import {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-community";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal/ConfirmationModal";
import { getAllNotifications } from "../../../Redux/Notifications/NotificationsActions";
import { hasAdminPermission } from "../../../Util/adminPermissions";
import Loading from "../../../Components/Common/Loading/Loading";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import { AppDispatch, RootState } from "../../../Redux/store";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import styles from "./Notifications.module.scss";
import { Link, useNavigate } from "react-router";
import { HiDotsVertical } from "react-icons/hi";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";

interface NotificationsType {
  notificationId: number;
  title: string;
  message: string;
  messageSent: boolean;
  tags: string[] | null;
  buildingId: number;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  createdBy: number;
  modifiedAt: string;
  modifiedBy: number;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function Notifications() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    notifications,
    notificationsLoading,
    deleteNotificationByIdLoading,
    publishNotificationLoading,
    publishNotificationError,
    publishNotificationData,
  } = useSelector((state: RootState) => state.notifications);
  const { adminPermissions } = useSelector((state: RootState) => state.auth);
  const canManage = hasAdminPermission(adminPermissions, "notification_manage");
  const [rowData, setRowData] = useState<NotificationsType[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [deleteNotificationId, setDeleteNotificationId] = useState<
    number | null
  >(null);
  const [publishNotificationId, setPublishNotificationId] = useState<
    number | null
  >(null);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState("");

  const filteredRowData = rowData?.filter(
    (item: NotificationsType) =>
      item?.title?.toLowerCase()?.includes(search?.toLowerCase()) ||
      item?.message?.toLowerCase()?.includes(search?.toLowerCase()),
  );

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const deleteNotificationHandler = (id: string) => {
    setDeleteNotificationId(Number(id));
    setIsDeleteModalOpen(true);
  };

  const deleteNotificationConfirmationHandler = () => {
    if (deleteNotificationId) {
      dispatch(
        deleteNotificationById({
          id: deleteNotificationId,
          successHandler: () => {
            setIsDeleteModalOpen(false);
            dispatch(getAllNotifications());
          },
        }),
      );
    }
  };

  const publishNotificationConfirmationHandler = () => {
    if (publishNotificationId && !publishNotificationLoading) {
      dispatch(
        publishNotification({
          notificationId: publishNotificationId,
          messageSent: true,
          successHandler: () => {
            setPublishNotificationId(null);
            setIsPublishModalOpen(false);
            dispatch(getAllNotifications());
          },
        }),
      );
    }
  };

  const publishNotificationHandler = (id: number) => {
    setPublishNotificationId(id);
    setIsPublishModalOpen(true);
  };

  const columnDefs: ColDef<NotificationsType>[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
    },
    {
      field: "message",
      headerName: "Message",
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      valueGetter: (params: ValueGetterParams<NotificationsType>) => {
        return moment(params?.data?.createdAt).format("DD/MM/YYYY hh:mm A");
      },
      flex: 1,
    },

    {
      headerName: "Published",
      valueGetter: (params: ValueGetterParams<NotificationsType>) => {
        return params?.data?.messageSent ? "Yes" : "No";
      },
      flex: 1,
    },

    ...(canManage
      ? [
          {
            sortable: false,
            suppressAutoSize: true,
            suppressMovable: true,
            suppressNavigable: true,
            editable: false,
            width: 40,
            cellStyle: {
              pointerEvents: "all",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            },
            cellRenderer: (params: ICellRendererParams) => {
              if (params?.data?.messageSent) {
                return null;
              }

              return (
                <div className={styles.iconContainer}>
                  <Menu
                    direction="left"
                    menuButton={
                      <MenuButton>
                        <HiDotsVertical className={styles.icon} />
                      </MenuButton>
                    }
                    transition
                    portal
                  >
                    <MenuItem
                      onClick={() =>
                        publishNotificationHandler(params?.data?.notificationId)
                      }
                    >
                      Publish
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        navigate(
                          `/admin/notifications/edit/${params?.data?.notificationId}`,
                        )
                      }
                    >
                      Edit
                    </MenuItem>

                    <MenuItem
                      onClick={() =>
                        deleteNotificationHandler(
                          params?.data?.notificationId?.toString(),
                        )
                      }
                    >
                      Delete
                    </MenuItem>
                  </Menu>
                </div>
              );
            },
          } as ColDef<NotificationsType>,
        ]
      : []),
  ];

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getAllNotifications());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (notifications) {
      setRowData(notifications);
    }
  }, [notifications]);

  return (
    <React.Fragment>
      {notificationsLoading && <Loading />}
      <div className={styles.mainContainer}>
        <div className={styles.topContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Notifications</h1>
          </div>
          <div className={styles.buttonContainer}>
            {canManage && (
              <Link
                to="/admin/notifications/add"
                className={styles.buttonContainer}
              >
                <button className={styles.button}>Add Notification</button>
              </Link>
            )}
          </div>
        </div>
        <div className={styles.bodyContainer}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              className={styles.input}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e?.target?.value || "")}
            />
            <div className={styles.iconContainer} onClick={() => setSearch("")}>
              <IoCloseSharp className={styles.icon} />
            </div>
          </div>

          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50, 100]}
              animateRows={true}
              domLayout="autoHeight"
            />
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        onConfirm={deleteNotificationConfirmationHandler}
        heading="Delete Notification"
        text="Are you sure you want to delete this notification?"
        loading={deleteNotificationByIdLoading}
      />
       <ConfirmationModal
        isOpen={isPublishModalOpen}
        setIsOpen={setIsPublishModalOpen}
        onConfirm={publishNotificationConfirmationHandler}
        heading="Publish Notification"
        text="Are you sure you want to publish this notification?"
        loading={publishNotificationLoading}
      />
    </React.Fragment>
  );
}

export default Notifications;
